import { useEffect, useState } from "react";
import { Mail, Send, Clock, ToggleLeft, ToggleRight, Eye, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 75 — Configuración del Resumen Diario.
 *
 * Permite al admin:
 *   - Activar/desactivar el envío automático.
 *   - Configurar la hora de envío (zona Europe/Madrid).
 *   - Ver una preview con las 4 métricas del día actual.
 *   - Pulsar "Enviar resumen ahora" para enviarlo inmediatamente a todos los admins.
 *
 * La plantilla del email (asunto + body_html + logo + pie) se edita en
 * Configuración → Diseño → Emails → "daily_summary".
 */
export default function DailySummaryTab() {
  const [config, setConfig] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [hour, setHour] = useState(20);
  const [minute, setMinute] = useState(0);
  const [enabled, setEnabled] = useState(false);

  const loadConfig = async () => {
    try {
      const r = await api.get("/daily-summary/config");
      setConfig(r.data);
      setHour(Number(r.data?.hour ?? 20));
      setMinute(Number(r.data?.minute ?? 0));
      setEnabled(!!r.data?.enabled);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadPreview = async () => {
    try {
      const r = await api.get("/daily-summary/preview");
      setPreview(r.data);
    } catch (e) { /* admin only - ignored if not authorized */ }
  };

  useEffect(() => { loadConfig(); loadPreview(); }, []);

  const saveConfig = async (next) => {
    setBusy(true);
    try {
      const r = await api.put("/daily-summary/config", next);
      setConfig(r.data);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const toggleEnabled = () => {
    const newVal = !enabled;
    setEnabled(newVal);
    saveConfig({ enabled: newVal });
  };

  const saveHour = () => saveConfig({ hour, minute });

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/daily-summary/send-now");
      const n = r.data?.sent_to?.length || 0;
      toast.success(`Resumen enviado a ${n} administrador${n === 1 ? "" : "es"}`);
      await loadConfig();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSending(false); }
  };

  if (!config) return <div className="text-sm text-[#525252]">Cargando…</div>;

  return (
    <div className="space-y-5" data-testid="daily-summary-tab">
      <div className="ds-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display font-bold text-base inline-flex items-center gap-2">
              <Mail size={18} /> Resumen diario automático
            </div>
            <p className="text-sm text-[#525252] mt-1 max-w-2xl">
              Envío diario por Gmail API a <b>todos los usuarios con rol administrador</b> con las 4 métricas clave del día:
              cierre de caja (cobros reales del día por forma de pago), reparaciones nuevas, equipos entregados y clientes nuevos.
            </p>
          </div>
          <button
            type="button"
            onClick={toggleEnabled}
            disabled={busy}
            role="switch"
            aria-checked={enabled}
            data-testid="daily-summary-toggle"
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#E5E5E0] hover:bg-[#F7F7F4] font-semibold"
          >
            {enabled
              ? <><ToggleRight size={16} className="text-[#6E8B3D]" /> Activo</>
              : <><ToggleLeft size={16} className="text-[#A8A8A0]" /> Desactivado</>}
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-[#E5E5E0] p-3">
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] inline-flex items-center gap-1.5">
              <Clock size={11} /> Hora de envío (Europe/Madrid)
            </label>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number" min={0} max={23}
                value={hour}
                onChange={(e) => setHour(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                data-testid="daily-summary-hour"
                className="w-20 ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm font-mono"
              />
              <span className="font-mono text-lg">:</span>
              <input
                type="number" min={0} max={59}
                value={minute}
                onChange={(e) => setMinute(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                data-testid="daily-summary-minute"
                className="w-20 ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm font-mono"
              />
              <button
                type="button" onClick={saveHour} disabled={busy}
                data-testid="daily-summary-save-hour"
                className="ml-2 px-3 py-2 text-xs uppercase tracking-wider bg-[#111110] text-white hover:bg-[#2a2a28] font-semibold disabled:bg-[#A8A8A0]"
              >Guardar</button>
            </div>
            {config.last_sent_at && (
              <div className="mt-3 text-xs text-[#525252] inline-flex items-center gap-1">
                <Check size={12} className="text-[#6E8B3D]" />
                Último envío: {new Date(config.last_sent_at).toLocaleString("es-ES")} · {config.last_sent_to_count || 0} destinatarios
              </div>
            )}
          </div>

          <div className="border border-[#6E8B3D] bg-[#F4F8EC] p-3 flex flex-col">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D] font-semibold inline-flex items-center gap-1.5">
              <Send size={11} /> Envío manual
            </div>
            <p className="text-xs text-[#525252] mt-1.5 flex-1">
              Envía el resumen del día actual ahora mismo a todos los admins.
            </p>
            <button
              type="button"
              onClick={sendNow}
              disabled={sending}
              data-testid="daily-summary-send-now"
              className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-[#6E8B3D] text-white hover:bg-[#556C2E] font-semibold disabled:bg-[#A8A8A0]"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sending ? "Enviando…" : "Enviar resumen ahora"}
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="ds-card p-5">
          <div className="font-display font-bold text-sm inline-flex items-center gap-2 mb-3">
            <Eye size={14} /> Vista previa de hoy ({preview.summary.date_label})
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="💰 Total cobrado" value={fmtEur(preview.summary.billing.total)} testid="preview-total-facturado" accent />
            <Stat label="🔧 Reparaciones" value={preview.summary.repairs_new} testid="preview-repairs-new" />
            <Stat label="📦 Entregados" value={preview.summary.devices_delivered} testid="preview-devices-delivered" />
            <Stat label="👥 Clientes nuevos" value={preview.summary.new_customers} testid="preview-new-customers" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs" data-testid="preview-cash-buckets">
            <Bucket label="Efectivo" v={preview.summary.billing.by_method.Efectivo} />
            <Bucket label="Tarjeta" v={preview.summary.billing.by_method.Tarjeta} />
            <Bucket label="Transferencia" v={preview.summary.billing.by_method.Transferencia} />
            <Bucket label="Otros" v={preview.summary.billing.by_method.Otros} />
            <Bucket label="Devoluciones" v={-(preview.summary.billing.refunds || 0)} />
          </div>
          <p className="text-[11px] text-[#525252] mt-2" data-testid="preview-cash-note">
            {preview.summary.billing.num_movimientos} cobros con fecha de cobro ese día · solo cuentan facturas cobradas (y abonos con forma de pago), no las emitidas.
            {preview.summary.billing.control && !preview.summary.billing.control.ok && (
              <span className="text-[#9B2C1F] font-semibold"> Descuadre detectado: {fmtEur(preview.summary.billing.control.diferencia)}</span>
            )}
          </p>
          <div className="mt-4 border border-[#E5E5E0]">
            <div className="px-3 py-1.5 bg-[#F7F7F4] border-b border-[#E5E5E0] font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              Vista previa del email
            </div>
            <iframe
              title="email-preview"
              srcDoc={preview.body_html}
              className="w-full bg-white"
              style={{ height: 540, border: "none" }}
              data-testid="daily-summary-preview-iframe"
            />
          </div>
          <p className="text-xs text-[#525252] mt-3">
            Edita la plantilla (asunto, HTML, logo, pie) en <b>Configuración → Diseño → Emails → daily_summary</b>.
          </p>
        </div>
      )}
    </div>
  );
}

function fmtEur(n) {
  const s = (Number(n) || 0).toFixed(2);
  const [int, dec] = s.split(".");
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "," + dec + " €";
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className={`border p-3 ${accent ? "border-[#6E8B3D] bg-[#F4F8EC]" : "border-[#E5E5E0]"}`} data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{label}</div>
      <div className={`font-display text-2xl font-black mt-1 ${accent ? "text-[#6E8B3D]" : "text-[#111110]"}`}>{value}</div>
    </div>
  );
}

function Bucket({ label, v }) {
  return (
    <div className="border border-[#E5E5E0] p-2.5">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{label}</div>
      <div className="font-display font-bold text-base mt-0.5">{fmtEur(v)}</div>
    </div>
  );
}
